/**
 * Indian national holidays — V4 F-5.
 *
 * Shipped in the repo rather than fetched: a workspace calendar must not
 * depend on a third party being up, and the list changes once a year.
 *
 * **Gazetted holidays only** — the three that are fixed by date (Republic Day,
 * Independence Day, Gandhi Jayanti) plus the major festivals, whose dates move
 * with the lunar calendar and are therefore listed per year rather than
 * computed. Everything here is editable after loading, because every company
 * observes a different subset and most add their own.
 */

export interface HolidayPreset {
  /** `MM-DD` for fixed dates; full `YYYY-MM-DD` for the movable ones. */
  date: string;
  name: string;
}

/** Same date every year. */
const FIXED: HolidayPreset[] = [
  { date: '01-26', name: 'Republic Day' },
  { date: '08-15', name: 'Independence Day' },
  { date: '10-02', name: 'Gandhi Jayanti' },
];

/**
 * Movable festivals, by year. Lunar dates cannot be computed from a formula
 * anyone should trust in a scheduling system, so they are listed.
 */
const MOVABLE: Record<number, HolidayPreset[]> = {
  2026: [
    { date: '2026-01-01', name: "New Year's Day" },
    { date: '2026-03-04', name: 'Holi' },
    { date: '2026-03-21', name: 'Id-ul-Fitr' },
    { date: '2026-03-26', name: 'Ram Navami' },
    { date: '2026-05-01', name: 'Buddha Purnima' },
    { date: '2026-05-27', name: 'Id-ul-Zuha (Bakrid)' },
    { date: '2026-08-26', name: 'Janmashtami' },
    { date: '2026-09-14', name: 'Ganesh Chaturthi' },
    { date: '2026-10-20', name: 'Dussehra' },
    { date: '2026-11-08', name: 'Diwali' },
    { date: '2026-11-24', name: 'Guru Nanak Jayanti' },
    { date: '2026-12-25', name: 'Christmas Day' },
  ],
  2027: [
    { date: '2027-01-01', name: "New Year's Day" },
    { date: '2027-03-22', name: 'Holi' },
    { date: '2027-03-11', name: 'Id-ul-Fitr' },
    { date: '2027-04-15', name: 'Ram Navami' },
    { date: '2027-05-20', name: 'Buddha Purnima' },
    { date: '2027-05-17', name: 'Id-ul-Zuha (Bakrid)' },
    { date: '2027-09-14', name: 'Janmashtami' },
    { date: '2027-10-03', name: 'Ganesh Chaturthi' },
    { date: '2027-10-09', name: 'Dussehra' },
    { date: '2027-10-28', name: 'Diwali' },
    { date: '2027-11-13', name: 'Guru Nanak Jayanti' },
    { date: '2027-12-25', name: 'Christmas Day' },
  ],
};

/** Years the preset can offer. */
export function presetYears(): number[] {
  return Object.keys(MOVABLE)
    .map(Number)
    .sort((a, b) => a - b);
}

/**
 * The full list for a year, sorted. Returns the three fixed days alone if the
 * year's movable dates have not been added yet — better a short honest list
 * than a wrong one.
 */
export function indianHolidays(year: number): { date: string; name: string }[] {
  const fixed = FIXED.map((h) => ({ date: `${year}-${h.date}`, name: h.name }));
  const movable = MOVABLE[year] ?? [];
  return [...fixed, ...movable].sort((a, b) => a.date.localeCompare(b.date));
}
